import { tryCatch } from '@/external/api'
import { IApi } from '@/store/spi/api.ts'
import { PublicRoutes } from '@repo/shared/routes-public'
import axios, { AxiosInstance } from 'axios'

export class AxiosAPI {
  private axios: AxiosInstance

  constructor(baseUrl: string) {
    this.axios = axios.create({
      baseURL: baseUrl + '/',
    })
  }

  api = (): IApi => {
    return {
      feedback: {
        createOne: async ({
          beneficiaryId,
          messageId,
          feedback,
        }: {
          beneficiaryId: string
          messageId: string
          feedback: string
        }) => {
          const res = await tryCatch(
            async () =>
              await this.axios.post<PublicRoutes.feedbacks.createOne.Response>(
                PublicRoutes.feedbacks.createOne.call({ beneficiaryId }),
                { messageId, feedback },
              ),
          )
          const created = res?.data
          if (!created) return null
          return { created }
        },
      },
      beneficiaries: {
        getInitialValuesFromOne: async ({ beneficiaryId }) => {
          const beneficiaryResponse = await tryCatch(
            async () =>
              await this.axios.get<PublicRoutes.beneficiaries.getOne.Response>(
                PublicRoutes.beneficiaries.getOne.call({ beneficiaryId }),
              ),
          )
          const beneficiary = beneficiaryResponse?.data.beneficiary
          if (!beneficiary) return null

          const organizationResponse = await tryCatch(
            async () =>
              await this.axios.get<PublicRoutes.beneficiaries.getOrganizationFromOne.Response>(
                PublicRoutes.beneficiaries.getOrganizationFromOne.call({
                  beneficiaryId,
                }),
              ),
          )
          const organization = organizationResponse?.data.organization
          if (!organization) return null

          const chatSessionsResponse = await tryCatch(
            async () =>
              await this.axios.get<PublicRoutes.beneficiaries.getChatSessionsFromOne.Response>(
                PublicRoutes.beneficiaries.getChatSessionsFromOne.call({
                  beneficiaryId,
                }),
              ),
          )
          const chatSessions = chatSessionsResponse?.data.chatSessions
          if (!chatSessions) return null

          const tasksResponse = await tryCatch(
            async () =>
              await this.axios.get<PublicRoutes.tasks.get.Response>(
                PublicRoutes.tasks.get.call({ beneficiaryId }),
              ),
          )
          const tasks = tasksResponse?.data.tasks
          if (!tasks) return null

          return {
            beneficiary: {
              fullName: beneficiary.fullName,
              id: beneficiary.id,
              language: beneficiary.language,
              profile: beneficiary.profile.value,
            },
            chatSessions: chatSessions.map((s) => ({
              beneficiaryId: s.beneficiaryId,
              closedAt: s.closedAt,
              id: s.id,
              startedAt: s.startedAt,
            })),
            organization: {
              chatSettings: organization.chatSettings,
              id: organization.id,
              language: organization.language,
              name: organization.name,
              texts: organization.texts ?? undefined,
            },
            tasks: tasks.map((t) => ({
              archivedAt: t.archivedAt,
              beneficiaryId: t.beneficiaryId,
              cta: t.cta
                ? {
                    link: t.cta.link,
                    name: t.cta.name,
                  }
                : undefined,
              categories: t.categories,
              content: t.content,
              createdAt: t.createdAt,
              dueDate: t.dueDate,
              durationInHours: t.durationInHours,
              id: t.id,
              title: t.title,
            })),
          }
        },

        patchOne: async ({ beneficiaryId, beneficiaryFields }) => {
          const beneficiaryResponse = await tryCatch(
            async () =>
              await this.axios.patch<PublicRoutes.beneficiaries.patchOne.Response>(
                PublicRoutes.beneficiaries.patchOne.call({ beneficiaryId }),
                {
                  beneficiaryFields,
                } satisfies PublicRoutes.beneficiaries.patchOne.Request,
              ),
          )
          const beneficiary = beneficiaryResponse?.data.beneficiary
          if (!beneficiary) return null
          return {
            beneficiary: {
              fullName: beneficiary.fullName,
              id: beneficiary.id,
              language: beneficiary.language,
              profile: beneficiary.profile.value,
            },
          }
        },
      },

      chatSessions: {
        createOne: async ({ beneficiaryId }) => {
          const chatSessionResponse = await tryCatch(
            async () =>
              await this.axios.post<PublicRoutes.chatSessions.createOne.Response>(
                PublicRoutes.chatSessions.createOne.call({ beneficiaryId }),
                {
                  beneficiaryId,
                } satisfies PublicRoutes.chatSessions.createOne.Request,
              ),
          )
          const chatSession = chatSessionResponse?.data.chatSession
          if (!chatSession) return null
          return {
            chatSession: {
              beneficiaryId: chatSession.beneficiaryId,
              closedAt: chatSession.closedAt,
              id: chatSession.id,
              startedAt: chatSession.startedAt,
            },
          }
        },

        getInitialValuesFromOne: async ({ chatSessionId }) => {
          const chatMessagesResponse = await tryCatch(
            async () =>
              await this.axios.get<PublicRoutes.chatSessions.getMessagesFromOne.Response>(
                // FIXME: we should at least pass the organizationId and beneficiaryId here to make sure we are not leaking data
                PublicRoutes.chatSessions.getMessagesFromOne.call({
                  chatSessionId,
                }),
              ),
          )
          const chatMessages = chatMessagesResponse?.data.chatMessages
          if (!chatMessages) return null
          return {
            chatMessages: chatMessages.map((m) => ({
              content: m.content,
              id: m.id,
              sender: m.sender,
              sentAt: m.sentAt,
              streaming: false,
            })),
          }
        },
      },

      tasks: {
        markOneAsDone: async ({ beneficiaryId, taskId }) => {
          const taskResponse = await tryCatch(
            async () =>
              await this.axios.patch<PublicRoutes.tasks.markOneAsDone.Response>(
                PublicRoutes.tasks.markOneAsDone.call({ beneficiaryId }),
                { taskId } satisfies PublicRoutes.tasks.markOneAsDone.Request,
              ),
          )
          const t = taskResponse?.data.task
          if (!t) return null
          return {
            task: {
              archivedAt: t.archivedAt,
              beneficiaryId: t.beneficiaryId,
              categories: t.categories,
              content: t.content,
              createdAt: t.createdAt,
              dueDate: t.dueDate,
              durationInHours: t.durationInHours,
              id: t.id,
              title: t.title,
            },
          }
        },
      },
    }
  }
}
